import { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  MapPin,
  Navigation,
  Send,
  Power,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Smartphone,
  RefreshCw,
  Download
} from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

type TrackingPosition = {
  latitude: number;
  longitude: number;
  speed: number | null;
  accuracy: number | null;
  timestamp: Date;
};

type TrackingStats = {
  totalSent: number;
  lastSuccess: Date | null;
  failedAttempts: number;
};

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

function getGeolocationErrorMessage(error: GeolocationPositionError): string {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return 'تم رفض إذن الموقع. فعّل الموقع من إعدادات المتصفح ثم اضغط إعادة المحاولة.';
    case error.POSITION_UNAVAILABLE:
      return 'الموقع غير متاح حاليًا. تأكد من تشغيل GPS ووجود اتصال مناسب ثم أعد المحاولة.';
    case error.TIMEOUT:
      return 'انتهت مهلة الحصول على الموقع. تأكد من تشغيل GPS ثم أعد المحاولة.';
    default:
      return 'تعذر الحصول على الموقع. تحقق من إعدادات الجهاز ثم أعد المحاولة.';
  }
}

export default function DelegateTrackingPage() {
  const { user } = useAuth();
  const [isTracking, setIsTracking] = useState(false);
  const [currentPosition, setCurrentPosition] = useState<TrackingPosition | null>(null);
  const [lastSent, setLastSent] = useState<Date | null>(null);
  const [error, setError] = useState('');
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showInstall, setShowInstall] = useState(false);
  const [stats, setStats] = useState<TrackingStats>({
    totalSent: 0,
    lastSuccess: null,
    failedAttempts: 0
  });

  const watchIdRef = useRef<number | null>(null);
  const sendIntervalRef = useRef<number | null>(null);
  const positionRef = useRef<TrackingPosition | null>(null);
  const trackingRef = useRef(false);
  const delegateId = user?.delegateId || user?.id || 1;

  const clearTrackingHandles = useCallback(() => {
    if (watchIdRef.current !== null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (sendIntervalRef.current !== null) {
      window.clearInterval(sendIntervalRef.current);
      sendIntervalRef.current = null;
    }
    trackingRef.current = false;
    setIsTracking(false);
  }, []);

  const sendLocation = useCallback(async () => {
    const position = positionRef.current;
    if (!position || !trackingRef.current) return;

    try {
      await api.post('/delegatelocations', {
        delegateId,
        latitude: position.latitude,
        longitude: position.longitude,
        speed: position.speed,
        accuracy: position.accuracy,
        timestamp: position.timestamp
      });

      setStats((previous) => ({
        totalSent: previous.totalSent + 1,
        lastSuccess: new Date(),
        failedAttempts: 0
      }));
      setLastSent(new Date());
    } catch (sendError) {
      console.error('Failed to send location:', sendError);
      setStats((previous) => ({
        ...previous,
        failedAttempts: previous.failedAttempts + 1
      }));
    }
  }, [delegateId]);

  const startTracking = useCallback(() => {
    if (!navigator.geolocation) {
      setError('المتصفح لا يدعم تحديد الموقع. استخدم متصفحًا يدعم GPS.');
      return;
    }

    clearTrackingHandles();
    setError('');
    trackingRef.current = true;
    setIsTracking(true);

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const nextPosition: TrackingPosition = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          speed: position.coords.speed,
          accuracy: position.coords.accuracy,
          timestamp: new Date(position.timestamp)
        };
        positionRef.current = nextPosition;
        setCurrentPosition(nextPosition);
        setError('');
        void sendLocation();
      },
      (geolocationError) => {
        console.error('Error getting location:', geolocationError);
        clearTrackingHandles();
        setError(getGeolocationErrorMessage(geolocationError));
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );

    sendIntervalRef.current = window.setInterval(() => {
      void sendLocation();
    }, 10000);
  }, [clearTrackingHandles, sendLocation]);

  const stopTracking = useCallback(() => {
    clearTrackingHandles();
    positionRef.current = null;
    setCurrentPosition(null);
  }, [clearTrackingHandles]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setShowInstall(false);
  };

  useEffect(() => {
    const handler = (event: Event) => {
      const installEvent = event as BeforeInstallPromptEvent;
      event.preventDefault();
      setDeferredPrompt(installEvent);
      setShowInstall(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => () => stopTracking(), [stopTracking]);

  const getTimeSinceLastSent = () => {
    if (!lastSent) return 'لم يُرسل بعد';
    const seconds = Math.floor((Date.now() - lastSent.getTime()) / 1000);
    if (seconds < 60) return `منذ ${seconds} ثانية`;
    return `منذ ${Math.floor(seconds / 60)} دقيقة`;
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div className="text-center">
        <h1 className="flex items-center justify-center gap-3 text-3xl font-bold text-gray-900">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600">
            <Smartphone className="h-6 w-6 text-white" />
          </div>
          <span>تتبع الموقع المباشر</span>
        </h1>
        <p className="mt-2 text-gray-500">شارك موقعك مع الإدارة بشكل تلقائي</p>
      </div>

      {showInstall && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="flex items-center justify-between gap-3 p-4">
            <div>
              <p className="font-semibold text-blue-900">ثبّت التطبيق للوصول السريع</p>
              <p className="text-sm text-blue-700">يساعد ذلك على استمرار التتبع أثناء العمل.</p>
            </div>
            <Button onClick={handleInstallClick} variant="outline" className="shrink-0">
              <Download className="ml-2 h-4 w-4" /> تثبيت
            </Button>
          </CardContent>
        </Card>
      )}

      <Card className={isTracking ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200'}>
        <CardContent className="p-6">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              {isTracking ? (
                <CheckCircle className="h-8 w-8 text-emerald-600" />
              ) : (
                <XCircle className="h-8 w-8 text-gray-400" />
              )}
              <div>
                <h3 className={`text-lg font-bold ${isTracking ? 'text-emerald-700' : 'text-gray-700'}`}>
                  {isTracking ? 'التتبع نشط' : 'التتبع متوقف'}
                </h3>
                <p className={`text-sm ${isTracking ? 'text-emerald-600' : 'text-gray-600'}`}>
                  {isTracking ? 'يتم إرسال موقعك تلقائيًا' : 'اضغط على الزر أدناه للبدء'}
                </p>
              </div>
            </div>
            <Badge variant={isTracking ? 'default' : 'secondary'} className="px-4 py-2 text-lg">
              {isTracking ? 'نشط' : 'متوقف'}
            </Badge>
          </div>

          <Button
            onClick={isTracking ? stopTracking : startTracking}
            variant={isTracking ? 'destructive' : 'default'}
            className={isTracking ? 'w-full' : 'w-full bg-emerald-600 hover:bg-emerald-700'}
            size="lg"
          >
            <Power className="ml-2 h-5 w-5" />
            {isTracking ? 'إيقاف التتبع' : 'بدء التتبع'}
          </Button>
        </CardContent>
      </Card>

      {error && (
        <Card className="border-red-300 bg-red-50">
          <CardContent className="flex items-start justify-between gap-3 p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-6 w-6 shrink-0 text-red-600" />
              <p className="text-red-700">{error}</p>
            </div>
            {!isTracking && navigator.geolocation && (
              <Button onClick={startTracking} variant="outline" className="shrink-0 border-red-300 text-red-700">
                <RefreshCw className="ml-2 h-4 w-4" /> إعادة المحاولة
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {currentPosition && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><MapPin className="h-5 w-5 text-blue-600" />موقعك الحالي</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="rounded-lg bg-gray-50 p-3"><p className="mb-1 text-sm text-gray-600">خط العرض</p><p className="font-mono text-lg font-bold">{currentPosition.latitude.toFixed(6)}</p></div>
              <div className="rounded-lg bg-gray-50 p-3"><p className="mb-1 text-sm text-gray-600">خط الطول</p><p className="font-mono text-lg font-bold">{currentPosition.longitude.toFixed(6)}</p></div>
            </div>
            {currentPosition.speed !== null && <div className="rounded-lg bg-blue-50 p-3"><p className="mb-1 text-sm text-blue-600">السرعة</p><p className="text-lg font-bold">{(currentPosition.speed * 3.6).toFixed(1)} كم/س</p></div>}
            {currentPosition.accuracy !== null && <div className="rounded-lg bg-gray-50 p-3"><p className="mb-1 text-sm text-gray-600">الدقة</p><p className="text-lg font-bold">±{currentPosition.accuracy.toFixed(0)} متر</p></div>}
            <div className="flex items-center gap-2 text-sm text-gray-600"><Clock className="h-4 w-4" /><span>تم التحديث: {currentPosition.timestamp.toLocaleTimeString('ar-EG')}</span></div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-indigo-600" />حالة الإرسال</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-lg bg-emerald-50 p-3 text-center"><p className="text-2xl font-bold text-emerald-700">{stats.totalSent}</p><p className="text-xs text-emerald-600">مرات الإرسال</p></div>
            <div className="rounded-lg bg-blue-50 p-3 text-center"><p className="text-sm font-bold text-blue-700">{lastSent ? lastSent.toLocaleTimeString('ar-EG') : '-'}</p><p className="text-xs text-blue-600">آخر إرسال</p></div>
            <div className="rounded-lg bg-red-50 p-3 text-center"><p className="text-2xl font-bold text-red-700">{stats.failedAttempts}</p><p className="text-xs text-red-600">محاولات فاشلة</p></div>
          </div>
          {lastSent && <div className="text-center text-sm text-gray-600">{getTimeSinceLastSent()}</div>}
        </CardContent>
      </Card>

      {currentPosition && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Navigation className="h-5 w-5 text-purple-600" />موقعك على الخريطة</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-hidden rounded-lg border-2 border-gray-200">
              <iframe
                width="100%"
                height="300"
                frameBorder="0"
                scrolling="no"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${currentPosition.longitude - 0.01}%2C${currentPosition.latitude - 0.01}%2C${currentPosition.longitude + 0.01}%2C${currentPosition.latitude + 0.01}&layer=mapnik&marker=${currentPosition.latitude}%2C${currentPosition.longitude}`}
                title="موقعك الحالي"
              />
            </div>
            <div className="mt-3 text-center"><a href={`https://www.google.com/maps?q=${currentPosition.latitude},${currentPosition.longitude}`} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">عرض في خرائط جوجل</a></div>
          </CardContent>
        </Card>
      )}

      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-4">
          <h4 className="mb-2 font-bold text-amber-800">نصائح مهمة</h4>
          <ul className="list-inside list-disc space-y-1 text-sm text-amber-700">
            <li>تأكد من تفعيل GPS على هاتفك</li>
            <li>اسمح للموقع من إعدادات المتصفح عند الطلب</li>
            <li>اترك الصفحة مفتوحة أثناء العمل</li>
            <li>سيتم إرسال موقعك تلقائيًا كل 10 ثوانٍ</li>
            <li>يمكنك إيقاف التتبع في أي وقت</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

// Note: speed from Geolocation API is meters per second; the UI converts it to km/h.
// The API payload keeps the original meters-per-second value for backend compatibility.
